import React, { useMemo, useState } from 'react';
import {
  COMPETITOR_COLOR, GRAPH, YOURS_COLOR, blendColor, initials, layoutNodes,
} from './competitiveUtils';

export const GRAPH_NODE_LIMIT = 80;

const venueAnchor = { yours: { x: 58, y: GRAPH.height / 2 }, competitor: { x: 942, y: GRAPH.height / 2 } };
const zoneLabel = { fontSize: 11, fontWeight: 600, fill: 'var(--sl-text-quaternary)', letterSpacing: '.06em', textTransform: 'uppercase' };

function edgePath(from, node) {
  const midX = (from.x + node.x) / 2;
  return `M${from.x},${from.y} C${midX},${from.y} ${midX},${node.y} ${node.x},${node.y}`;
}

function CompetitiveNetworkGraph({
  rows, level, yourName, competitorName, yourShows, competitorShows, selectedKey, onSelect,
}) {
  const [hoverKey, setHoverKey] = useState(null);
  const nodes = useMemo(
    () => layoutNodes(rows.slice().sort((a, b) => (b.yours + b.competitor) - (a.yours + a.competitor)).slice(0, GRAPH_NODE_LIMIT), level),
    [rows, level],
  );
  const activeKey = hoverKey || selectedKey;
  const agencyLevel = level === 'agency';
  const hovered = hoverKey && nodes.find((node) => node.key === hoverKey);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ position: 'relative', width: '100%', minWidth: 720 }}>
        <svg viewBox={`0 0 ${GRAPH.width} ${GRAPH.height}`} width="100%" style={{ display: 'block' }} role="img" aria-label={`Booking network: ${yourName} vs ${competitorName}`}>
          <rect x={150} y={24} width={240} height={GRAPH.height - 48} rx={12} fill={`color-mix(in srgb, ${YOURS_COLOR} 7%, transparent)`} />
          <rect x={610} y={24} width={240} height={GRAPH.height - 48} rx={12} fill={`color-mix(in srgb, ${COMPETITOR_COLOR} 7%, transparent)`} />
          <line x1={500} y1={30} x2={500} y2={GRAPH.height - 30} stroke="var(--sl-border-secondary)" strokeDasharray="4 4" />
          <text x={270} y={44} textAnchor="middle" style={zoneLabel}>{`Leans ${yourName}`}</text>
          <text x={500} y={44} textAnchor="middle" style={zoneLabel}>Contested</text>
          <text x={730} y={44} textAnchor="middle" style={zoneLabel}>{`Leans ${competitorName}`}</text>

          {nodes.map((node) => {
            const on = activeKey === node.key;
            const off = activeKey && !on;
            return [['yours', node.yours, YOURS_COLOR], ['competitor', node.competitor, COMPETITOR_COLOR]].map(([side, count, color]) => (
              count > 0 && (
                <path
                  key={`${node.key}-${side}`}
                  d={edgePath(venueAnchor[side], node)}
                  fill="none"
                  stroke={color}
                  strokeWidth={Math.min(agencyLevel ? 12 : 6, 0.8 + count * (agencyLevel ? 0.32 : 0.55))}
                  strokeOpacity={on ? 0.75 : off ? 0.05 : 0.22}
                  strokeLinecap="round"
                />
              )
            ));
          })}

          {[['yours', yourName, YOURS_COLOR, yourShows], ['competitor', competitorName, COMPETITOR_COLOR, competitorShows]].map(([side, name, color, shows]) => (
            <g key={side}>
              <circle cx={venueAnchor[side].x} cy={venueAnchor[side].y} r={34} fill={color} stroke="var(--sl-bg-primary)" strokeWidth={4} />
              <text x={venueAnchor[side].x} y={venueAnchor[side].y + 5} textAnchor="middle" fontSize={14} fontWeight={700} fill="#fff">{initials(name) || name.slice(0, 2)}</text>
              <text x={venueAnchor[side].x} y={venueAnchor[side].y + 56} textAnchor="middle" fontSize={13} fontWeight={600} fill="var(--sl-text-primary)">{name}</text>
              <text x={venueAnchor[side].x} y={venueAnchor[side].y + 72} textAnchor="middle" fontSize={11} fill="var(--sl-text-tertiary)">{`${shows} shows`}</text>
            </g>
          ))}

          {nodes.slice().sort((a, b) => (a.key === activeKey) - (b.key === activeKey)).map((node) => {
            const on = activeKey === node.key;
            const off = activeKey && !on;
            const selected = selectedKey === node.key;
            const gap = node.yours === 0;
            const showLabel = agencyLevel || node.total >= 6 || on;
            return (
              <g
                key={node.key}
                role="button"
                tabIndex={0}
                aria-label={`${node.name}: ${node.yours} at ${yourName}, ${node.competitor} at ${competitorName}`}
                style={{ cursor: 'pointer', opacity: off ? 0.3 : 1, transition: 'opacity .15s' }}
                onMouseEnter={() => setHoverKey(node.key)}
                onMouseLeave={() => setHoverKey(null)}
                onClick={() => onSelect(selected ? null : node.key)}
                onKeyDown={(event) => { if (event.key === 'Enter') onSelect(selected ? null : node.key); }}
              >
                {gap && <circle cx={node.x} cy={node.y} r={node.radius + 4} fill="none" stroke="var(--sl-fg-error)" strokeWidth={1.5} strokeDasharray="3 3" />}
                <circle cx={node.x} cy={node.y} r={node.radius} fill={blendColor(node)} stroke={selected ? 'var(--sl-text-primary)' : 'var(--sl-bg-primary)'} strokeWidth={selected ? 2.5 : 2} />
                {agencyLevel && node.radius >= 13 && (
                  <text x={node.x} y={node.y + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff" style={{ pointerEvents: 'none' }}>{node.total}</text>
                )}
                {showLabel && (
                  <text
                    x={node.x}
                    y={node.y + node.radius + (gap ? 17 : 14)}
                    textAnchor="middle"
                    fontSize={agencyLevel ? 12 : 10.5}
                    fontWeight={on ? 700 : 600}
                    fill="var(--sl-text-secondary)"
                    stroke="var(--sl-bg-primary)"
                    strokeWidth={3}
                    paintOrder="stroke"
                    style={{ pointerEvents: 'none' }}
                  >
                    {node.name}
                  </text>
                )}
              </g>
            );
          })}

          <text x={150} y={GRAPH.height - 8} fontSize={11} fill="var(--sl-text-quaternary)">{`← 100% ${yourName}`}</text>
          <text x={850} y={GRAPH.height - 8} fontSize={11} fill="var(--sl-text-quaternary)" textAnchor="end">{`100% ${competitorName} →`}</text>
        </svg>
        {hovered && (
          <div
            style={{
              position: 'absolute',
              left: `${hovered.x / 10}%`,
              top: `${((hovered.y - hovered.radius) / GRAPH.height) * 100}%`,
              transform: 'translate(-50%, calc(-100% - 4px))',
              background: 'var(--sl-bg-primary-solid)',
              color: '#fff',
              borderRadius: 8,
              padding: '8px 12px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 2,
              fontSize: 12,
              lineHeight: '18px',
            }}
          >
            <div style={{ fontWeight: 600 }}>{level === 'agent' ? `${hovered.name} · ${hovered.agency || 'Unknown agency'}` : hovered.name}</div>
            <div style={{ opacity: 0.8 }}>{`${yourName} ${hovered.yours} · ${competitorName} ${hovered.competitor}`}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompetitiveNetworkGraph;
